import {action, observable} from 'mobx';
import s from '../s';
import {Portal} from '../../util';
import {InsigniaCustomization} from '../i';
import {isIconLinoProduct} from './lino';
import {scrollToPreview} from './preview-scroll';

// The insignia (the seal) selection, shared by both product page layouts: the handlers, the reaction to a lino change, and the
// filter that decides which seals show. Moved unchanged from components/insignia/insignia.tsx, whose Insignia keeps its collapse.

/**
 * The seal list's state that is not product state: the seal category filter (fpc_id: -1 the uncategorized seals, -2 every seal, or a
 * category the CAF dropdown picked) and whether that dropdown shows. Reset when the seal list is created (resetInsigniaView).
 */
export const insigniaView = observable({fpc_id: -1, show_dropdown: true});

export const resetInsigniaView = action((): void => {
    insigniaView.fpc_id = s.ps.is_caf ? -2 : -1;
    insigniaView.show_dropdown = true;
});

/** Icon types show seal names without the colour picker's dropdown unless the linos span more than one grad school. */
export const insigniaIconUi = (): boolean => {
    const uniqueGradSchoolIds = new Set(s.prod.linos.flatMap(item => item.gradSchoolIds ?? []).filter(id => id > 0));
    return isIconLinoProduct() && !(uniqueGradSchoolIds.size > 1);
};

/** Back to the product's own seal (or the seal shown for every grad school). */
export const restoreOriginalInsignia = action((origInsName: string = s.prod.orig_pcs.Base_InsigniaName): void => {
    s.ps.insignia_id = s.prod.InsigniaID;
    s.ps.insignia_base_name = origInsName;
    s.ps.insignia_dyf_color = 'gold';
    s.ps.insignia_is_dyf = false;
    s.ui.update_pricing = true;
    s.ui.doRender = true;

    const m = s.prod.insignias.filter(m => m.id === s.prod.InsigniaID || m.show_all_gs)[0];
    if (!m) {
        // The product's insignia is not among this store's choices: keep it as set above.
        s.pulse();
        return;
    }
    s.ps.insignia_id = m.id;
    s.ps.insignia_base_name = m.name;
    s.ps.insignia_is_dyf = m.isCAF;
    s.ps.insignia_dyf_color = m.ins_color.toLowerCase();
    s.ui.name_insignia = m.name.toLowerCase();
    s.ui.title_insignia = m.title;
    s.ui.doRender = true;
    s.ui.update_pricing = true;

    s.pulse();

    scrollToPreview('frame-display');
});

/** The seal list's first render: the CAF category dropdown's visibility, and the product's own seal applied. */
export const initInsigniaSelection = action((iconUi: boolean = insigniaIconUi(), origInsName?: string): void => {
    if (s.prod.IsCAF) {
        insigniaView.show_dropdown = true;
    } else if (s.prod.InsigniaID < 1 && s.prod.insignias.length > 0) {
        insigniaView.show_dropdown = false;
    } else if (iconUi) {
        insigniaView.show_dropdown = false;
    }

    if (!s.prod.IsCAF) {
        insigniaView.show_dropdown = false;
    }

    if (s.prod.InsigniaID > 0) {
        restoreOriginalInsignia(origInsName);
    }
});

export const selectInsignia = action((d_i: number): void => {
    const m = s.prod.insignias.filter(m => m.id === d_i)[0];
    if (m.id === s.prod.InsigniaID) {
        restoreOriginalInsignia();
        return;
    }
    s.ps.insignia_id = m.id;
    s.ps.insignia_base_name = m.name;
    s.ps.insignia_is_dyf = m.isCAF;
    s.ps.insignia_dyf_color = m.ins_color.toLowerCase();
    s.ui.name_insignia = m.name.toLowerCase();
    s.ui.title_insignia = m.title;
    s.ui.doRender = true;
    s.ui.update_pricing = true;
    s.pulse();

    scrollToPreview('prod-header');
});

export const removeInsignia = action((): void => {
    s.ps.insignia_id = -1;
    s.ui.title_insignia = '';
    s.ui.update_pricing = true;
    s.ui.doRender = true;
    s.pulse();
});

export const setInsigniaCategory = action((fpcId: number): void => {
    insigniaView.fpc_id = fpcId;
});

/**
 * A lino change (lino_changed) re-picks the seal for the new grad school: the current seal when the school still offers it,
 * else the school's first seal in a colour the mat and lino allow, else the product's own seal.
 */
export const insigniaLinoChanged = action((): void => {
    if (!s.prod.insignias || s.prod.insignias.length < 1 || s.prod.IsCAF) {
        return;
    } else if (s.prod.insignias.length === 1) {
        restoreOriginalInsignia();
        return;
    }

    if (s.prod.IsCAF) {
        return;
    }

    const currentId = s.ps.insignia_id;

    const _has_current_gs_insignias = s.currentGradSchoolID > 0 && s.prod.insignias.some(m => m.gradSchoolIds?.includes(s.currentGradSchoolID));

    const m = s.prod.insignias
        .filter(m => (m.gradSchoolIds.includes(s.currentGradSchoolID) || (!m.gradSchoolIds.some(id => id > 0) && !_has_current_gs_insignias) || m.show_all_gs) && m.instIds?.includes(s.insigniaInstitutionID))
        .filter(m => (s.ps.ins_gold_allowed && m.ins_color === 'gold') || (s.ps.ins_silver_allowed && m.ins_color === 'silver') || (s.ps.ins_black_allowed && m.ins_color === 'black') || (m.ins_color !== 'black' && m.ins_color !== 'silver' && m.ins_color !== 'gold'));
    if (m && m.length > 0) {
        const matching = m.find(insignia => insignia.id === currentId);
        selectInsignia(matching ? matching.id : m[0].id);
        return;
    } else {
        restoreOriginalInsignia();
        return;
    }
});

// ---- view model ------------------------------------------------------------------------------------------------------

/** product.tsx's mount rule for the seal section. */
export const insigniaSectionMounted = (): boolean => !s.IsPortal3 && !!s.prod && !!s.prod.insignias && (s.prod.insignias.length > 1 || (s.prod.InsigniaID < 1 && s.prod.insignias.length > 0));

/** Insignia's own guard: more than one choice, or a product without its own seal. */
export const insigniaListShown = (): boolean => !(!s || !s.ui.show_cust || !s.prod || !s.insigniaChoices.options || (s.insigniaChoices.options.length <= 1 && s.preferredInsigniaId(s.prod.InsigniaID) === s.prod.InsigniaID && !(s.prod.InsigniaID < 1 && s.insigniaChoices.options.length > 0)));

export const insigniaSectionShown = (): boolean => insigniaSectionMounted() && insigniaListShown();

/**
 * The seals the list shows: linen mats drop embossed seals, militaryframes.com drops category 24, the category filter
 * (insigniaView.fpc_id), the colours the mat and lino allow, and the current grad school's seals (or the general ones).
 */
export const visibleInsignias = (): InsigniaCustomization[] => {
    const _has_show_all_gs = s.insigniaChoices.options.some(m => m.show_all_gs === true);
    const _has_current_gs_insignias = s.currentGradSchoolID > 0 && s.insigniaChoices.options.some(m => m.gradSchoolIds?.includes(s.currentGradSchoolID));
    const fpc_id = insigniaView.fpc_id;

    return s.insigniaChoices.options
        .filter(m => (s.ui.linen && !m.title.toLowerCase().includes('embossed')) || !s.ui.linen)
        .filter(m => !(Portal?.PortalID === 2 && m.fpc_id === 24))
        .filter(
            m =>
                (m.fpc_id === fpc_id || fpc_id === -2) &&
                ((s.ps.ins_gold_allowed && m.ins_color === 'gold') || (s.ps.ins_silver_allowed && m.ins_color === 'silver') || (s.ps.ins_black_allowed && m.ins_color === 'black') || (m.ins_color !== 'black' && m.ins_color !== 'silver' && m.ins_color !== 'gold')),
        )
        .filter(m => (!s.prod.IsCAF && (m.gradSchoolIds.includes(s.currentGradSchoolID) || (!m.gradSchoolIds.some(id => id > 0) && !_has_current_gs_insignias))) || s.prod.IsCAF || m.show_all_gs || (_has_show_all_gs && m.id === s.prod.InsigniaID && !s.prod.IsCAF && s.currentGradSchoolID < 1));
};

/** The control's "Remove Insignia" button: a seal is picked on a CAF frame, or on a product whose own setup has none. */
export const insigniaRemovable = (): boolean => (s.ps.is_caf && s.ps.insignia_id > 0) || (!s.ps.is_caf && s.prod.InsigniaID < 1 && s.ps.insignia_id > 0);

/** The Top / Bottom position select (types 87 and 91; Top switches the frame to type 91). */
export const insigniaPositionShown = (): boolean => s.pcs.ProductTypeID === 87 || s.pcs.ProductTypeID === 91;
